import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";

const LINKS = [
  { to: "/doere", label: "Døre" },
  { to: "/vinduer", label: "Vinduer" },
  { to: "/cases", label: "Cases" },
  { to: "/om-os", label: "Om os" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const onHome = location.pathname === "/";
  const transparent = onHome && !scrolled;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-background/80 backdrop-blur-xl border-b border-border/60"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`font-display text-2xl md:text-3xl tracking-[0.2em] transition-colors ${
            transparent ? "text-white" : "text-foreground"
          }`}
        >
          HAGELUND
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-label font-medium transition-opacity hover:opacity-60 ${
                  transparent ? "text-white/90" : "text-foreground/80"
                } ${isActive ? "opacity-100" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/kontakt"
            data-testid="nav-cta"
            data-track="cta_click"
            className={`text-xs uppercase tracking-label font-semibold px-5 py-3 border transition-colors ${
              transparent
                ? "border-white/60 text-white hover:bg-white hover:text-foreground"
                : "border-foreground text-foreground hover:bg-foreground hover:text-background"
            }`}
          >
            Book rådgivning
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${transparent ? "text-white" : "text-foreground"}`}
          onClick={() => setOpen(true)}
          aria-label="Åbn menu"
        >
          <List size={28} weight="light" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-background md:hidden flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-border">
              <span className="font-display text-2xl tracking-[0.2em]">HAGELUND</span>
              <button onClick={() => setOpen(false)} aria-label="Luk menu" data-testid="mobile-menu-close">
                <X size={28} weight="light" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  <Link
                    to={l.to}
                    className="font-display text-5xl font-light py-2 block"
                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
